import { Roles } from "utils/roles";

/**
 * Core user profile interface based on your backend API specification
 * Represents the data structure for user profile completion
 */
export interface IUserProfile {
    entityId?: string;                      // UUID - Required for updates, optional for creation
    email: string;                          // Required - from Auth0
    firstName?: string | null;              // Optional
    lastName: string;                       // Required
    gender?: UserGender | null;             // Optional - informational for a practitioner
    function?: string | null;               // Optional - job function
    speciality?: string | null;             // Optional - medical speciality (free form)
    jobTitle?: string | null;               // Optional
    medicalEntity?: string | null;          // Optional - hospital/clinic
    city?: string | null;                   // Optional
    country?: string | null;                // Optional
    phone1?: string | null;                 // Unknown on a profile provisioned at login
    phone2?: string | null;                 // Optional - secondary phone
    profilePicture?: string | null;         // Optional - profile image URL
}

/**
 * Clinician profile from backend API (matches ClinicianOut schema)
 * Extends IUserProfile with backend-specific fields
 */
export interface IClinicianProfile extends IUserProfile {
    userId: string;
    roles: string[];                        // Array of roles from backend
    zcreationTimestamp: string;
    ztouchTimestamp: string;
}

/**
 * Form data interface for saving the profile (create or update)
 */
export interface IUserProfileUpdateData {
    entityId?: string | null;               // Absent on the very first save (no record yet);
                                            // the server keys the upsert on the auth token

    email: string;                          
    firstName?: string | null;              
    lastName: string;
    gender?: UserGender | null;
    function?: string | null;
    speciality?: string | null;             // Free form field
    jobTitle?: string | null;
    medicalEntity?: string | null;
    city?: string | null;
    country?: string | null;
    phone1?: string | null;
    phone2?: string | null;                 
}

/**
 * Gender enum values based on your API
 */
export const UserGenders = ['MALE', 'FEMALE', 'OTHER'] as const;
export type UserGender = typeof UserGenders[number];

/**
 * Profile status interface
 */
export interface IUserProfileStatus {
    status: UserProfileStatusType;
}

/**
 * Profile completion status types
 */
export const UserProfileStatuses = ['COMPLETED', 'NOT_COMPLETED'] as const;
export type UserProfileStatusType = typeof UserProfileStatuses[number];

/**
 * Countries list interface
 */
export interface ICountry {
    code: string;
    name: string;
    dialCode: string;
    flag: string;
}

/**
 * User roles constants
 */
export const UserRoles = {
    EXPERT: Roles.ROLE_DIPITA_EXPERT,
    REQUESTER: Roles.ROLE_DIPITA_REQUESTER,
    CARDIOLOGY: Roles.ROLE_DIPITA_CARDIOLOGY
} as const;

/**
 * Helper functions for role management
 */
export function hasRole(userRoles: string[], role: string): boolean {
    return (userRoles || []).includes(role);
}

export function isExpert(userRoles: string[]): boolean {
    return hasRole(userRoles, UserRoles.EXPERT);
}

export function getDefaultRedirectPath(userRoles: string[]): string {
    if (isExpert(userRoles)) {
        return '/dashboard/expert/pending-requests';
    }
    return '/dashboard/requester/request';
}